import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminEmailAPI } from '../../../../shared/services/emailAPI'
import { apiService } from '../../../../shared/services/api'

export default function EmailSendTab({
  onSendEmail,
  sendLoading,
  templates,
}: any) {
  const [emailForm, setEmailForm] = useState({
    toEmail: '',
    subject: '',
    htmlContent: '',
    templateId: '',
  })
  const [recipientType, setRecipientType] = useState<'manual' | 'user'>(
    'manual'
  )
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [showUserSelector, setShowUserSelector] = useState(false)

  // 사용자 목록 데이터
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () =>
      apiService.client.get('/admin/users').then(res => res.data.data),
    enabled: recipientType === 'user',
  })

  // 템플릿 선택 시 자동 반영
  const handleTemplateChange = (templateId: string) => {
    const selectedTemplate = templates?.find((t: any) => t._id === templateId)
    if (selectedTemplate) {
      setEmailForm(prev => ({
        ...prev,
        templateId,
        subject: selectedTemplate.subject,
        htmlContent: selectedTemplate.htmlContent,
      }))
    } else {
      setEmailForm(prev => ({
        ...prev,
        templateId: '',
        subject: '',
        htmlContent: '',
      }))
    }
  }

  // 사용자 선택 토글
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  // 모든 사용자 선택/해제
  const toggleAllUsers = () => {
    if (selectedUsers.length === usersData?.users?.length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers(usersData?.users?.map((user: any) => user._id) || [])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // 수신자 검증
    if (recipientType === 'manual') {
      if (!emailForm.toEmail) {
        alert('수신자 이메일을 입력해주세요.')
        return
      }
    } else {
      if (selectedUsers.length === 0) {
        alert('발송할 사용자를 선택해주세요.')
        return
      }
    }

    if (!emailForm.subject) {
      alert('제목을 입력해주세요.')
      return
    }

    try {
      if (recipientType === 'manual') {
        // 개별 메일 발송
        await onSendEmail({
          toEmail: emailForm.toEmail,
          subject: emailForm.subject,
          htmlContent: emailForm.htmlContent || '<p>테스트 메일입니다.</p>',
          textContent: emailForm.htmlContent
            ? emailForm.htmlContent.replace(/<[^>]*>/g, '')
            : '테스트 메일입니다.',
          templateId: emailForm.templateId || undefined,
        })
      } else {
        // 선택된 사용자들에게 벌크 메일 발송
        const selectedUserEmails =
          usersData?.users
            ?.filter((user: any) => selectedUsers.includes(user._id))
            ?.map((user: any) => user.email) || []

        await adminEmailAPI.sendBulkEmail({
          recipients: selectedUserEmails,
          subject: emailForm.subject,
          htmlContent: emailForm.htmlContent || '<p>테스트 메일입니다.</p>',
          textContent: emailForm.htmlContent
            ? emailForm.htmlContent.replace(/<[^>]*>/g, '')
            : '테스트 메일입니다.',
          templateId: emailForm.templateId || undefined,
          campaignName: `선택된 사용자 발송 - ${new Date().toISOString().split('T')[0]}`,
        })
      }

      alert('메일이 성공적으로 발송되었습니다!')
      setEmailForm({
        toEmail: '',
        subject: '',
        htmlContent: '',
        templateId: '',
      })
      setSelectedUsers([])
    } catch (error) {
      console.error('메일 발송 오류:', error)
      alert('메일 발송에 실패했습니다.')
    }
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
      <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
        메일 발송
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 수신자 타입 선택 */}
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
            수신자 선택 방법
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="manual"
                checked={recipientType === 'manual'}
                onChange={e =>
                  setRecipientType(e.target.value as 'manual' | 'user')
                }
                className="mr-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                수동 입력
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="user"
                checked={recipientType === 'user'}
                onChange={e =>
                  setRecipientType(e.target.value as 'manual' | 'user')
                }
                className="mr-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                기존 사용자 선택
              </span>
            </label>
          </div>
        </div>

        {/* 수신자 입력 */}
        {recipientType === 'manual' ? (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              수신자 이메일 *
            </label>
            <input
              type="email"
              value={emailForm.toEmail}
              onChange={e =>
                setEmailForm(prev => ({ ...prev, toEmail: e.target.value }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="recipient@example.com"
              required
            />
          </div>
        ) : (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                사용자 선택 * ({selectedUsers.length}명 선택됨)
              </label>
              <button
                type="button"
                onClick={() => setShowUserSelector(!showUserSelector)}
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
              >
                {showUserSelector ? '닫기' : '사용자 선택'}
              </button>
            </div>

            {showUserSelector && (
              <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700">
                {usersLoading ? (
                  <div className="py-4 text-center">
                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      사용자 목록 로딩 중...
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-2 dark:border-gray-600">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        총 {usersData?.users?.length || 0}명의 사용자
                      </span>
                      <button
                        type="button"
                        onClick={toggleAllUsers}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        {selectedUsers.length === usersData?.users?.length
                          ? '전체 해제'
                          : '전체 선택'}
                      </button>
                    </div>

                    <div className="space-y-2">
                      {usersData?.users?.map((user: any) => (
                        <label
                          key={user._id}
                          className="flex cursor-pointer items-center rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-600"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user._id)}
                            onChange={() => toggleUserSelection(user._id)}
                            className="mr-3"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {user.name || user.email}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {user.email}
                              {user.name &&
                                ` • 가입일: ${new Date(user.createdAt).toLocaleDateString()}`}
                            </div>
                          </div>
                          <div className="text-xs text-gray-400">
                            {user.stats?.totalUrls || 0} 링크
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* 템플릿 선택 */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            템플릿 선택 (선택사항)
          </label>
          <select
            value={emailForm.templateId}
            onChange={e => handleTemplateChange(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="">직접 입력</option>
            {templates?.map((template: any) => (
              <option key={template._id} value={template._id}>
                {template.name} - {template.templateType}
              </option>
            ))}
          </select>
          {emailForm.templateId && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              템플릿 선택 시 제목과 내용이 자동으로 입력됩니다.
            </p>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            제목 *
          </label>
          <input
            type="text"
            value={emailForm.subject}
            onChange={e =>
              setEmailForm(prev => ({ ...prev, subject: e.target.value }))
            }
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            placeholder="메일 제목을 입력하세요"
            required
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            내용 (HTML)
          </label>
          <textarea
            value={emailForm.htmlContent}
            onChange={e =>
              setEmailForm(prev => ({ ...prev, htmlContent: e.target.value }))
            }
            rows={8}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            placeholder="<p>메일 내용을 HTML로 입력하세요.</p>"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={sendLoading}
            className={`rounded-lg px-4 py-2 font-medium text-white ${
              sendLoading
                ? 'cursor-not-allowed bg-gray-400'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
            }`}
          >
            {sendLoading
              ? '발송 중...'
              : recipientType === 'user' && selectedUsers.length > 1
                ? `${selectedUsers.length}명에게 발송`
                : '메일 발송'}
          </button>

          <button
            type="button"
            onClick={() => {
              setEmailForm({
                toEmail: '',
                subject: '',
                htmlContent: '',
                templateId: '',
              })
              setSelectedUsers([])
            }}
            className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            초기화
          </button>
        </div>
      </form>
    </div>
  )
}

// 템플릿 폼 모달 컴포넌트 - 개발중
